<template>
	<div
		class="custom-input form-control flex-center"
		:class="{
			'error-input': !isValid || (!isValidHex && type === 'color'),
			'disable-input': disabled,
			'is-focus': isFocus,
		}"
		:id="elementId">
		<!-- Prefix -->
		<div class="custom-input-prefix disabled">{{ label }}</div>

		<!-- Text -->
		<template v-if="type === 'text'">
			<input
				class="custom-input-mid"
				:class="[`text-${align}`, { 'pl-3': align === 'left' }]"
				:type="type"
				:value="value"
				@focus="onFocus"
				@blur="onBlur"
				@input="onChange"
				@keyup.enter="onEnter"
				:maxlength="max"
				@keydown="textKeydown($event)"
				:readonly="isReadOnly"
				v-tooltip.bottom-end="{ content: 'The asset name cannot contain special characters/symbols', classes: 'tooltip-symbol', show: isSpecialCharacter && restrictChar, trigger: 'manual' }"
			/>
		</template>

		<!-- Number -->
		<template v-if="type === 'number'">
			<scrubber
				class="custom-input-mid"
				:min="min"
				:max="max"
				:steps="step"
				:value="value"
				:disabled="disable || disabled"
				@focus="onFocus"
				@blur="onBlur"
				@input="onNumberChange"
				@keyenter="onEnterNumberChange"
				:allowNegative="allowNegative"
			/>
		</template>

		<!-- Number -->
		<template v-if="type === 'duration'">
			<scrubber-masked
				class="custom-input-mid"
				:min="'0'"
				:max="'216000'"
				:steps="step"
				:value="value"
				:disabled="disable || disabled"
				@focus="onFocus"
				@blur="onBlur"
				@input="onNumberChange"
				@keyenter="onEnterNumberChange"
			/>
		</template>
		<template v-if="type === 'duration-timer'">
			<scrubber-masked-timer
				class="custom-input-mid"
				:min="minTime"
				:max="maxTime"
				:steps="step"
				:value="value"
				:disabled="disable || disabled"
				@focus="onFocus"
				@blur="onBlur"
				@input="onNumberChange"
				@keyenter="onEnterNumberChange"
			/>
		</template>

		<!-- select -->
		<template v-if="type === 'select'">
			<input
				:class="[`text-${align}`, { 'pl-3': align === 'left' }]"
				class="custom-input-mid capitalize"
				:value="value"
				@focus="onFocus"
				@blur="onBlur"
				@input="onChange"
				@keyup.enter="onEnter"
				:keydown="false"
				disabled
			/>
			<i class="material-icons arrow-select-property">expand_more</i>
		</template>
		<template v-if="type === 'selectLeft'">
			<input
				:class="[`text-left`, { 'pl-3': align === 'left' }]"
				class="custom-input-mid capitalize ml-3"
				:value="value"
				@focus="onFocus"
				@blur="onBlur"
				@input="onChange"
				@keyup.enter="onEnter"
				:keydown="false"
				disabled
			/>
			<i class="material-icons arrow-select-property">expand_more</i>
		</template>
		<!-- select with search -->
		<template v-if="type === 'selectSearch'">
			<input
				:class="[`text-${align}`, { 'pl-3': align === 'left' }, {'truncate-font-timer': align === 'left'}, {'truncate-font-custom-command': align === 'center'}]"
				class="custom-input-mid capitalize pl-4 pb-0"
				:value="value"
				@focus="onFocus"
				@blur="onBlur"
				@input="onChange"
				@keyup.enter="onEnter"
				disabled
			/>
			<i v-if="showClose" @click.stop="removeFont()" class="material-icons arrow-select-property">cancel</i>
		</template>
		<template v-if="type === 'selectSearchEmpty'">
			<input
				:class="[`text-left`, { 'pl-3': align === 'left' }]"
				class="custom-input-mid capitalize truncate-font-timer"
				:value="value"
				@focus="onFocus"
				@blur="onBlur"
				@input="onChange"
				@keyup.enter="onEnter"
			/>
			<i class="material-icons arrow-select-property">expand_more</i>
		</template>
		<!-- Color -->
		<template v-if="type === 'color'">
			<span v-if="!isValidHex" class="absolute error-color-input">{{$t('Incorrect Value')}}</span>
			<input
				maxlength=9
				class="custom-input-mid uppercase"
				:class="[`text-${align}`, { 'pl-3': align === 'left' }]"
				type="text"
				:value="value"
				@click="openColorPicker"
				@input="onChange"
				@keyup.enter="onEnter"
				@blur="onBlur"
			/>
		</template>

		<!-- Suffix -->
		<div class="custom-input-suffix">
			<template v-if="field !== 'index'">
				<span v-if="type !== 'color'">{{ suffix }}</span>
				<template v-else>
					<!-- color picker -->
					<div
						class="color-preview"
						:style="swatchStyle"
						@click="openColorPicker"
					/>
					<div
						class="color-picker"
						:class="`color-picker-${pickerPosition}`"
						v-if="isColorPickerVisible">
						<chrome-picker
							@input="onColorChange"
							v-model="color"
							@change="textColor"
						></chrome-picker>
						<button
							@click="closeColorPicker"
							class="btn btn-primary btn-close-picker w-100"
						>{{ $t('Close') }}</button>
					</div>
				</template>
			</template>
			<template v-if="field === 'index'">
				<mdIcon
					class="tooltip-indexing"
					v-tooltip.bottom-end="{ content: duplicateLayerText, classes: 'tooltip-indexing' }"
					v-if="indexDuplicated" >layers</mdIcon>
				<mdIcon class="tooltip-index-nonduplicate" v-else>layers</mdIcon>
			</template>
		</div>
	</div>
</template>

<script>
import { Chrome } from 'vue-color';
import ClickOutside from 'vue-click-outside';
import Scrubber from '@/components/Scrubber.vue';
import ScrubberMasked from '@/components/ScrubberMasked.vue';
import ScrubberMaskedTimer from '@/components/ScrubberMaskedTimer.vue';

import { duplicateVar, generateRandomId } from '../lib/helper';

export default {
	name: 'CustomInput',
	props: {
		value: {},
		type: {
			type: String,
			default: 'text',
		},
		label: {
			type: String,
		},
		suffix: {
			type: String,
			default: '',
		},
		field: {
			type: String,
			default: 'normal',
		},
		required: {
			type: Boolean,
			default: false,
		},
		disable: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		align: {
			type: String,
			default: 'center',
		},
		min: {
			type: Number,
			default: 1,
		},
		max: {
			default: 100,
		},
		maxTime: {
			default: 0,
		},
		minTime: {
			type: String,
		},
		step: {
			type: Number,
			default: 1,
		},
		pickerPosition: {
			type: String,
			default: 'left',
		},
		indexDuplicated: {
			type: Boolean,
			default: false,
		},
		isEnableBorder: {
		},
		restrictChar: {
		},
		showClose: {
		},
		allowNegative: {
			type: Boolean,
			default: false,
		},
		elementH: {
			type: Boolean,
			default: false,
		},
		isReadOnly: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		Scrubber,
		ScrubberMasked,
		ScrubberMaskedTimer,
		ChromePicker: Chrome,
	},
	directives: {
		ClickOutside,
	},
	data() {
		return {
			color: '',
			isColorPickerVisible: false,
			uuid: '',
			isFocus: false,
			duplicateLayerText: 'Other item also using this layer, only one item at a time will be displayed',
			maskedValue: '',
			isSpecialCharacter: false,
		};
	},
	methods: {
		textColor() {
			// console.log('yayaya');
		},
		onEnter(e) {
			let value = e.target.value;
			if (!this.elementH) value = value.trim();
			this.$emit('keyenter', value);

			if (this.type === 'color') {
				this.color = duplicateVar(value);
			}
		},
		onEnterNumberChange(value) {
			this.$emit('keyenter', value);
		},
		onChange(e) {
			let value = e.target.value;
			if (!this.elementH) value = value.trim();
			this.$emit('input', value);

			if (this.type === 'color') {
				this.color = duplicateVar(value);
			}
		},
		onNumberChange(value) {
			this.$emit('input', value);
		},
		onColorChange(color) {
			const hex = color.hex8;
			this.color = hex;
			this.$emit('input', hex);
		},
		openColorPicker() {
			this.isFocus = true;
			this.isColorPickerVisible = true;
		},
		closeColorPicker() {
			this.$emit('blur');
			this.isFocus = false;
			this.isColorPickerVisible = false;
		},
		initData() {
			this.uuid = generateRandomId();
			if (this.type === 'color') {
				if (this.value) this.color = duplicateVar(this.value);
			}
			if (this.type === 'duration') {
				const finalValue = new Date(this.stringToSeconds(this.value) * 1000).toISOString().substr(14, 5);
				this.maskedValue = finalValue;
			}
		},
		stringToSeconds(sec) {
			const hms = sec; // your input string
			const a = hms.split(':'); // split it at the colons
			// minutes are worth 60 seconds. Hours are worth 60 minutes.
			const seconds = (+a[0]) * 60 + (+a[1]);
			return seconds;
		},
		onClick({ target }) {
			if (!target.closest(`#${this.elementId}`)) {
				this.isFocus = false;
				this.isColorPickerVisible = false;
			}
		},
		onFocus() {
			this.isFocus = true;
		},
		onBlur() {
			this.isFocus = false;
			this.$emit('blur');
			this.isSpecialCharacter = false;
		},
		textKeydown(e) {
			if (this.restrictChar) {
				if (/[^A-Z0-9 ]/gi.test(e.key)) {
					e.preventDefault();
					this.isSpecialCharacter = true;
				} else {
					this.isSpecialCharacter = false;
				}
			}
		},
		removeFont() {
			this.$emit('remove');
		},

	},
	computed: {
		isValid() {
			return this.required ? this.value !== '' : true;
		},
		isValidHex() {
			return /^#[0-9A-F]{6,8}$/i.test(this.value);
		},
		isCustom() {
			return this.customPosition;
		},
		swatchStyle() {
			return {
				backgroundColor: this.value,
			};
		},
		elementId() {
			return `custom-input-${this.uuid}`;
		},
	},
	watch: {
		isEnableBorder() {
			if (!this.isEnableBorder) this.isColorPickerVisible = false;
		},
	},
	created() {
		this.initData();
		window.addEventListener('click', this.onClick);
	},
	destroyed() {
		window.removeEventListener('click', this.onClick);
	},
};
</script>

<style>
</style>
